import { useQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { DateTime } from 'luxon';
import { FC, useState } from 'react';
import styled from 'styled-components';

import AddButton from '@/components/AddButton';
import { SrOnly } from '@/components/Layout/Parts';
import ScrollableTable from '@/components/ScrollableTable';
import { FullWidthRow, Table, TableContent, Th } from '@/components/Table';
import Text from '@/components/Text';
import ChannelFormModal from '@/pages/Settings/components/ChannelFormModal';
import ChannelsTableRow from '@/pages/Settings/components/ChannelsTableRow';
import { ChannelResource } from '@/types/resources';
import { api } from '@/utils/api';

interface Props {
  competitors?: boolean;
  activeMonth: DateTime;
  teamId: string;
}

const StyledTable = styled(Table)`
  th {
    &:nth-child(1) {
      width: 220px;
    }
    &:nth-child(2) {
      width: 140px;
    }
    &:nth-child(3) {
      width: 220px;
    }
    &:nth-child(4) {
      width: 110px;
    }
    &:nth-child(5) {
      width: 160px;
    }
    &:nth-child(6) {
      width: 180px;
    }
    &:nth-child(7) {
      width: 130px;
    }
    &:nth-child(8) {
      width: 180px;
    }
  }
  th,
  td {
    &:first-child {
      position: sticky;
      left: 0;
    }
  }
  tr:last-child {
    td {
      &:first-child {
        position: static;
      }
    }
  }
`;

const Channels: FC<Props> = ({ competitors = false, activeMonth, teamId }) => {
  const [showAddModal, setShowAddModal] = useState(false);

  const { data, isError, isLoading } = useQuery<ChannelResource[]>(
    [
      'team_channels',
      {
        teamId,
        competitors: competitors ? 1 : 0,
        month: activeMonth.toFormat('yyyy-MM'),
      },
    ],
    () =>
      api.get<never, ChannelResource[]>(`/v1/teams/${teamId}/channels`, {
        params: {
          ...(competitors && { competitors: 1 }),
          month: activeMonth.toFormat('yyyy-MM'),
        },
      }),
    {
      staleTime: 120 * 1000,
    },
  );

  return (
    <section>
      <ScrollableTable>
        <StyledTable $fixed>
          <thead>
            <tr>
              <Th>
                Channel name
                <Text as="div" $type="extraSmall" $color="gray">
                  Channel link
                </Text>
              </Th>
              <Th>Identifier</Th>
              <Th>Channel Tags</Th>
              <Th>Subscribers</Th>
              <Th>Monthly Total Views</Th>
              <Th>Monthly Published Views</Th>
              <Th>Monthly Avg V7</Th>
              <Th>Notes</Th>
              <Th $actionsCol>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!data?.length}
            >
              {data?.map((channel) => (
                <ChannelsTableRow
                  key={channel.id}
                  channel={channel}
                  activeMonth={activeMonth.toFormat('yyyy-MM')}
                  isCompetitorChannel={competitors}
                />
              ))}
            </TableContent>
            <FullWidthRow>
              <AddButton onClick={() => setShowAddModal(true)}>
                Add {competitors ? 'competitor channel' : 'company channel'}
              </AddButton>
            </FullWidthRow>
          </tbody>
        </StyledTable>
      </ScrollableTable>
      <AnimatePresence>
        {showAddModal && (
          <ChannelFormModal
            activeMonth={activeMonth.toFormat('yyyy-MM')}
            onClose={() => setShowAddModal(false)}
            competitor={competitors ? 1 : 0}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

export default Channels;
